/**
 * avatars/blocky-avatar-outfit.js
 * Mixin quản lý trang phục, tà áo dài và quần cho BlockyAvatar.
 */

import { generateProceduralTexture, getDefaultShirtColor } from './avatar-presets.js';
import { attachAoDaiFlaps, attachOutfitExtras, removeAoDaiFlaps, updateAoDaiFlaps } from './ao-dai-flaps.js';

class OutfitHolder {
  attachAoDaiFlaps(shirtColor = this.config.colors.shirt) {
    attachAoDaiFlaps(this, shirtColor);
  }

  attachOutfitExtras(outfitId) {
    attachOutfitExtras(this, outfitId);
  }

  removeAoDaiFlaps() {
    removeAoDaiFlaps(this);
  }

  /**
   * Thay đổi trang phục động trên 3D runtime
   * @param {string} outfitId - 'ao_dai' | 'ao_ba_ba' | 'hoc_sinh_vn' | 'grab' | 'be' | 'xanh_sm' | 'so_mi' | 'suit' | 'ao_ba_lo' | 'lab_coat' | 'default'
   * @param {string} shirtColor - màu áo tuỳ chọn
   */
  setOutfit(outfitId, shirtColor = null) {
    this.config.features.outfit = outfitId;
    if (shirtColor) {
      this.config.colors.shirt = shirtColor;
    } else {
      this.config.colors.shirt = getDefaultShirtColor(outfitId);
    }

    // Vẽ lại texture thân trước
    const newCanvas = generateProceduralTexture('torso', {
      shirtColor: this.config.colors.shirt,
      skinColor: this.config.colors.skin,
      outfit: outfitId,
      isLabCoat: outfitId === 'lab_coat'
    });

    if (this.torsoTex) {
      const ctx = this.torsoCanvas.getContext('2d');
      ctx.clearRect(0, 0, this.torsoCanvas.width, this.torsoCanvas.height);
      ctx.drawImage(newCanvas, 0, 0);
      this.torsoTex.needsUpdate = true;
    }

    // Vẽ lại texture thân sau (đặc biệt hỗ trợ khoét lưng thể thao cho áo ba lỗ)
    const newBackCanvas = generateProceduralTexture('torso_back', {
      shirtColor: this.config.colors.shirt,
      skinColor: this.config.colors.skin,
      outfit: outfitId
    });

    if (this.torsoBackTex) {
      const ctx = this.torsoBackCanvas.getContext('2d');
      ctx.clearRect(0, 0, this.torsoBackCanvas.width, this.torsoBackCanvas.height);
      ctx.drawImage(newBackCanvas, 0, 0);
      this.torsoBackTex.needsUpdate = true;
    }

    // CẬP NHẬT MÀU HÔNG VÀ VAI (luôn lấy màu vải áo của trang phục mới, không dùng nhầm màu da!)
    if (this.materials && this.materials.torsoSideMat) {
      const sideCol = outfitId === 'lab_coat' ? '#f8fafc' : this.config.colors.shirt;
      this.materials.torsoSideMat.color.set(sideCol);
    }

    // Cập nhật màu tay áo (với áo ba lỗ: toàn bộ cánh tay chuyển thành cánh tay trần màu da!)
    if (this.materials && this.materials.sleeveMat) {
      const sleeveCol =
        outfitId === 'ao_ba_lo'
          ? this.config.colors.skin
          : outfitId === 'lab_coat'
            ? '#f8fafc'
            : this.config.colors.shirt;
      this.materials.sleeveMat.color.set(sleeveCol);
    }

    // Chi tiết 3D theo trang phục (tà áo dài, vạt blouse, mũ hoodie, tạp dề...) & quần đi kèm
    this.config.features.isLabCoat = outfitId === 'lab_coat';
    this.attachOutfitExtras(outfitId);
    if (outfitId === 'ao_dai') {
      this.setPants('silk', '#ffffff');
    } else {
      if (outfitId === 'hoc_sinh_vn') {
        this.setPants('long', '#1e3a8a');
      } else if (outfitId === 'ao_ba_ba') {
        this.setPants('long', '#18181b');
      }
    }
    this.enforceShadowPolicy();
  }

  /**
   * Thay đổi kiểu quần / váy động
   */
  setPants(pantsType = 'long', pantsColor = null) {
    let col = pantsColor;
    if (!col) {
      if (pantsType === 'silk_white' || pantsType === 'silk') col = '#ffffff';
      else if (pantsType === 'short') col = '#475569';
      else if (pantsType === 'jean') col = '#1d4ed8';
      else if (pantsType === 'long') col = '#1e293b';
      else if (pantsType === 'black') col = '#0f172a';
    }
    if (col && this.materials && this.materials.pantsMat) {
      this.materials.pantsMat.color.set(col);
      this.config.colors.pants = col;
    }
    this.enforceShadowPolicy();
  }

  updateAoDaiFlaps() {
    updateAoDaiFlaps(this);
  }
}

export function registerAvatarOutfitMethods(proto) {
  Object.getOwnPropertyNames(OutfitHolder.prototype).forEach((name) => {
    if (name !== 'constructor') {
      Object.defineProperty(proto, name, {
        value: OutfitHolder.prototype[name],
        writable: true,
        configurable: true,
        enumerable: false
      });
    }
  });
}
